/**
 * ITER 71 — Accordion inline riepilogo richiesta.
 *
 * Sostituisce il vecchio link "Dettaglio" che apriva `/admin/richieste?id=X`.
 * L'operatore vede subito i "fattori" cliccati dal cliente nel wizard
 * senza cambiare pagina.
 *
 * Sezioni renderizzate:
 *   • Dati compilati dal cliente (ramo-specifici: veicolo, coperture, profilo)
 *   • Dati contatto (nome, CF con toggle mostra, email, telefono, pref contatto)
 *   • Consensi IVASS (badge verde/rosso + timestamp + policy version)
 *   • Cliente unificato (badge 🔥 se ricorrente + link storia)
 *   • Documenti (caricato/non caricato)
 *   • Task correlati (snippet + count)
 *   • Azioni operatore (prendi in carico, cambio stato, task, dettaglio esteso)
 *
 * Endpoint: GET /api/admin/convenzioni/crm/richieste/{id}/riepilogo
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, CheckCircle2, XCircle, Copy, ExternalLink, Printer,
  Car, Home, Palmtree, ClipboardList, ShieldCheck, FileText, Users,
  Flame, ArrowUpRight, User as UserIcon, Phone, Mail, MapPin, AlertTriangle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MACRO_ICON = { auto: Car, famiglia: Home, tempo_libero: Palmtree };

export default function RichiestaRiepilogoAccordion({ richiestaId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError("");
      try {
        const { data: d } = await axios.get(
          `${API}/admin/convenzioni/crm/richieste/${richiestaId}/riepilogo`,
          { withCredentials: true },
        );
        if (!cancelled) setData(d);
      } catch (e) {
        const msg = e.response?.data?.detail || "Errore caricamento riepilogo";
        if (!cancelled) { setError(msg); toast.error(msg); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [richiestaId]);

  if (loading) {
    return (
      <div className="bg-emerald-50/40 border-t border-emerald-100 px-6 py-6 flex items-center justify-center gap-2 text-sm text-slate-600"
           data-testid={`conv-crm-riepilogo-loading-${richiestaId}`}>
        <Loader2 className="w-4 h-4 animate-spin" /> Caricamento riepilogo…
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-red-50 border-t border-red-100 px-6 py-4 text-sm text-red-800"
           data-testid={`conv-crm-riepilogo-error-${richiestaId}`}>
        <AlertTriangle className="w-4 h-4 inline mr-1" /> {error}
      </div>
    );
  }
  if (!data) return null;

  const { sezioni } = data;
  const MacroIcon = MACRO_ICON[data.macro] || FileText;

  return (
    <div className="bg-gradient-to-br from-emerald-50/60 to-slate-50 border-t-2 border-emerald-200 px-5 py-4"
         data-testid={`conv-crm-riepilogo-${richiestaId}`}>
      {/* Header interno */}
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-emerald-100">
        <div className="flex items-center gap-2">
          <MacroIcon className="w-4 h-4 text-[#163321]" />
          <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
            Riepilogo richiesta cliente
          </span>
          <span className="text-[11px] text-slate-400">·</span>
          <span className="text-[11px] text-slate-500">{data.id.slice(0, 8)}</span>
        </div>
        <button onClick={onClose}
                className="text-[11px] text-slate-500 hover:text-[#163321]"
                data-testid={`conv-crm-riepilogo-close-${richiestaId}`}>
          Chiudi ▲
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* SEZIONE 1: Dati ramo-specifici */}
        <SezioneCard title="🎯 Dati compilati dal cliente" testid="riepilogo-sezione-dati-ramo">
          {data.macro === "auto"
            ? <AutoRamoBlock d={sezioni.dati_ramo} />
            : <FamigliaRamoBlock d={sezioni.dati_ramo} />}
        </SezioneCard>

        {/* SEZIONE 2: Contatto — ITER 72 tutto in chiaro, no toggle */}
        <SezioneCard title="👤 Dati contatto" testid="riepilogo-sezione-contatto">
          <ContattoBlock c={sezioni.contatto} />
        </SezioneCard>

        {/* SEZIONE 3: Consensi */}
        <SezioneCard title="📜 Consensi IVASS" testid="riepilogo-sezione-consensi">
          <ConsensiBlock c={sezioni.consensi} />
        </SezioneCard>

        {/* SEZIONE 4: Cliente unificato */}
        <SezioneCard title="🔗 Cliente unificato" testid="riepilogo-sezione-cliente-uni">
          <ClienteUnificatoBlock cu={sezioni.cliente_unificato} />
        </SezioneCard>

        {/* SEZIONE 5: Documenti */}
        <SezioneCard title="📎 Documenti" testid="riepilogo-sezione-documenti">
          <DocumentiBlock docs={sezioni.documenti} />
        </SezioneCard>

        {/* SEZIONE 6: Task correlati */}
        <SezioneCard title="✅ Task correlati" testid="riepilogo-sezione-task">
          <TaskBlock task={sezioni.task_correlati} richiestaId={data.id} />
        </SezioneCard>
      </div>

      {/* FOOTER: Azioni operatore */}
      <div className="mt-4 pt-3 border-t border-emerald-100 flex flex-wrap items-center gap-2"
           data-testid="riepilogo-azioni">
        {sezioni.azioni_disponibili.map((az) => (
          <AzioneButton key={az.testid} az={az} richiestaId={data.id} />
        ))}
        {/* ITER 72 — Stampa/PDF riepilogo (endpoint dedicato /pdf) */}
        <button
          type="button"
          disabled={pdfLoading}
          onClick={async () => {
            setPdfLoading(true);
            try {
              const resp = await axios.get(
                `${API}/admin/convenzioni/crm/richieste/${data.id}/riepilogo/pdf`,
                { withCredentials: true, responseType: "blob" },
              );
              const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
              const a = document.createElement("a");
              a.href = url;
              a.download = `riepilogo_${data.id.slice(0, 8)}.pdf`;
              document.body.appendChild(a); a.click();
              document.body.removeChild(a); URL.revokeObjectURL(url);
              toast.success("PDF scaricato");
            } catch (e) {
              toast.error(e.response?.data?.detail || "Errore generazione PDF");
            } finally { setPdfLoading(false); }
          }}
          className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-lg border border-[#D97B58] text-[#D97B58] hover:bg-[#D97B58] hover:text-white transition-all disabled:opacity-50"
          data-testid="conv-crm-action-stampa-pdf"
        >
          {pdfLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Printer className="w-3 h-3" />}
          Stampa/PDF riepilogo
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Sub-components
// ═══════════════════════════════════════════════════════════════════════
function SezioneCard({ title, children, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3.5 shadow-sm"
         data-testid={testid}>
      <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}

function DataRow({ label, value, testid }) {
  if (value === null || value === undefined || value === "") {
    return (
      <div className="flex items-baseline justify-between gap-3 py-0.5" data-testid={testid}>
        <span className="text-[11px] text-slate-500">{label}</span>
        <span className="text-[11px] text-slate-400 italic">Dato non fornito</span>
      </div>
    );
  }
  return (
    <div className="flex items-baseline justify-between gap-3 py-0.5" data-testid={testid}>
      <span className="text-[11px] text-slate-500">{label}</span>
      <span className="text-[12px] text-slate-800 font-medium truncate max-w-[60%] text-right">
        {typeof value === "boolean" ? (value ? "Sì" : "No") : value}
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Auto ramo (convenzioni_leads)
// ─────────────────────────────────────────────────────────────────
function AutoRamoBlock({ d }) {
  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-2 mb-1">
        <span className="inline-block px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-100 text-emerald-800">
          {d.sub_label || "Auto"}
        </span>
      </div>

      {/* Veicolo */}
      {d.veicolo && Object.keys(d.veicolo).length > 0 && (
        <div className="border-l-2 border-emerald-200 pl-2.5" data-testid="auto-veicolo">
          <div className="text-[10px] text-slate-500 uppercase mb-1">Veicolo</div>
          <DataRow label="Marca" value={d.veicolo.marca} />
          <DataRow label="Modello" value={d.veicolo.modello} />
          <DataRow label="Targa" value={d.veicolo.targa} />
          <DataRow label="Anno" value={d.veicolo.anno} />
          <DataRow label="Alimentazione" value={d.veicolo.alimentazione} />
        </div>
      )}

      {/* Coperture (badge list) */}
      {d.coperture && d.coperture.length > 0 && (
        <div className="border-l-2 border-emerald-200 pl-2.5" data-testid="auto-coperture">
          <div className="text-[10px] text-slate-500 uppercase mb-1">
            Coperture richieste ({d.coperture.length})
          </div>
          <div className="flex flex-wrap gap-1">
            {d.coperture.map((c) => (
              <span key={c.id}
                    className="inline-block px-2 py-0.5 rounded-full text-[11px] bg-blue-50 text-blue-800 border border-blue-100"
                    data-testid={`auto-copertura-${c.id}`}>
                {c.label}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Cambio veicolo */}
      {d.cambio_veicolo && (
        <div className="border-l-2 border-amber-200 pl-2.5" data-testid="auto-cambio-veicolo">
          <div className="text-[10px] text-amber-700 uppercase mb-1">Cambio veicolo</div>
          <DataRow label="Targa vecchio" value={d.cambio_veicolo.targa_vecchio} />
          <DataRow label="Sub-tipo" value={d.cambio_veicolo.sub_tipo} />
        </div>
      )}

      {/* Profilo assicurativo */}
      {d.profilo && Object.values(d.profilo).some((v) => v) && (
        <div className="border-l-2 border-emerald-200 pl-2.5" data-testid="auto-profilo">
          <div className="text-[10px] text-slate-500 uppercase mb-1">Profilo assicurativo</div>
          <DataRow label="Uso principale" value={d.profilo.uso_principale} />
          <DataRow label="Km/anno" value={d.profilo.km_anno_range} />
          <DataRow label="Classe CU" value={d.profilo.classe_cu} />
          <DataRow label="Sinistri 5 anni" value={d.profilo.sinistri_5anni} />
        </div>
      )}

      {/* Preferenze contatto */}
      {d.preferenze_contatto && (d.preferenze_contatto.canale || d.preferenze_contatto.fascia) && (
        <div className="border-l-2 border-emerald-200 pl-2.5" data-testid="auto-preferenze">
          <div className="text-[10px] text-slate-500 uppercase mb-1">Preferenze contatto</div>
          <DataRow label="Canale" value={d.preferenze_contatto.canale} />
          <DataRow label="Fascia oraria" value={d.preferenze_contatto.fascia} />
        </div>
      )}

      {d.note_cliente && (
        <div className="bg-yellow-50 border border-yellow-200 p-2 rounded text-[11px] text-yellow-900"
             data-testid="auto-note-cliente">
          <span className="font-semibold">Note cliente:</span> {d.note_cliente}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Famiglia/Tempo Libero ramo (richieste_assicurative)
// ─────────────────────────────────────────────────────────────────
function FamigliaRamoBlock({ d }) {
  const hasSpecifico = d.ramo_specifico && Object.keys(d.ramo_specifico).length > 0;
  const hasAltri = d.altri_fattori && Object.keys(d.altri_fattori).length > 0;
  const empty = !hasSpecifico && !hasAltri && !d.note_cliente;
  const isCasa = (d.sub_label || "").toLowerCase().includes("casa") ||
                 (d.tipo_richiesta || "").toLowerCase().includes("casa") ||
                 (d.ramo_specifico?.tipo_immobile);

  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-2">
        <span className="inline-block px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-100 text-emerald-800">
          {d.sub_label || "Famiglia"}
        </span>
        {d.tipo_richiesta && (
          <span className="text-[11px] text-slate-500">· {d.tipo_richiesta}</span>
        )}
      </div>

      {isCasa && d.ramo_specifico && (
        <CasaValutazioneAdminPanel datiWizard={d.ramo_specifico} />
      )}

      {hasSpecifico && (
        <div className="border-l-2 border-emerald-200 pl-2.5" data-testid="famiglia-specifico">
          <div className="text-[10px] text-slate-500 uppercase mb-1">Dati wizard</div>
          {Object.entries(d.ramo_specifico).map(([k, v]) => (
            <DataRow key={k}
                       label={k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                       value={renderValue(v)}
                       testid={`famiglia-field-${k}`} />
          ))}
        </div>
      )}

      {hasAltri && (
        <div className="border-l-2 border-slate-200 pl-2.5" data-testid="famiglia-altri">
          <div className="text-[10px] text-slate-500 uppercase mb-1">Altri fattori</div>
          {Object.entries(d.altri_fattori).map(([k, v]) => (
            <DataRow key={k}
                       label={k.replace(/_/g, " ")}
                       value={renderValue(v)} />
          ))}
        </div>
      )}

      {d.premio_stimato && (
        <div className="bg-emerald-50 border border-emerald-200 p-2 rounded text-[12px] text-emerald-900"
             data-testid="famiglia-premio">
          Premio stimato: <span className="font-semibold">{d.premio_stimato} €</span>
        </div>
      )}

      {d.note_cliente && (
        <div className="bg-yellow-50 border border-yellow-200 p-2 rounded text-[11px] text-yellow-900"
             data-testid="famiglia-note-cliente">
          <span className="font-semibold">Note cliente:</span> {d.note_cliente}
        </div>
      )}

      {empty && (
        <div className="text-[11px] text-slate-400 italic py-2" data-testid="famiglia-empty">
          Nessun dato specifico compilato oltre a contatti e consensi.
        </div>
      )}
    </div>
  );
}

function renderValue(v) {
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "object" && v !== null) return JSON.stringify(v);
  return v;
}

// ─────────────────────────────────────────────────────────────────
// Contatto — ITER 72 tutto in chiaro, no toggle mostra/nascondi
// ─────────────────────────────────────────────────────────────────
function ContattoBlock({ c }) {
  const copyToClipboard = (val, label) => {
    try {
      navigator.clipboard.writeText(val);
      toast.success(`${label} copiato negli appunti`);
    } catch { /* ignore */ }
  };
  const cfValue = c.cf || c.cf_full;
  const emailValue = c.email || c.email_full;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <UserIcon className="w-3.5 h-3.5 text-slate-400" />
        <span className="text-[13px] font-semibold text-slate-800">
          {c.nome || "—"} {c.cognome || ""}
        </span>
      </div>

      {cfValue && (
        <div className="flex items-center gap-2 text-[12px]" data-testid="contatto-cf">
          <span className="text-slate-500 w-16">CF:</span>
          <span className="font-mono text-slate-800 select-all flex-1">{cfValue}</span>
          <button onClick={() => copyToClipboard(cfValue, "CF")}
                  className="text-slate-400 hover:text-[#163321]"
                  data-testid="contatto-cf-copy" title="Copia CF">
            <Copy className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      <div className="flex items-center gap-2 text-[12px]" data-testid="contatto-email">
        <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        <a href={`mailto:${emailValue || ""}`}
           className="text-slate-800 truncate flex-1 hover:underline select-all">
          {emailValue || "—"}
        </a>
        {emailValue && (
          <button onClick={() => copyToClipboard(emailValue, "Email")}
                  className="text-slate-400 hover:text-[#163321]" title="Copia email">
            <Copy className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <div className="flex items-center gap-2 text-[12px]" data-testid="contatto-tel">
        <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        <a href={`tel:${c.telefono || ""}`}
           className="text-slate-800 flex-1 hover:underline select-all">
          {c.telefono || "—"}
        </a>
        {c.telefono && (
          <button onClick={() => copyToClipboard(c.telefono, "Telefono")}
                  className="text-slate-400 hover:text-[#163321]" title="Copia telefono">
            <Copy className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {(c.canale_pref || c.fascia_pref) && (
        <div className="mt-1 pt-1.5 border-t border-slate-100 text-[11px] text-slate-500">
          <span className="text-[10px] uppercase">Preferenze: </span>
          {c.canale_pref && <span className="inline-block px-1.5 rounded bg-slate-100 text-slate-700 ml-1">{c.canale_pref}</span>}
          {c.fascia_pref && <span className="inline-block px-1.5 rounded bg-slate-100 text-slate-700 ml-1">{c.fascia_pref}</span>}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Consensi
// ─────────────────────────────────────────────────────────────────
function ConsensiBlock({ c }) {
  const items = [
    { key: "privacy",      label: "Privacy contrattuale", required: true },
    { key: "mup",          label: "MUP IVASS",             required: true },
    { key: "marketing",    label: "Marketing",             required: false },
    { key: "profilazione", label: "Profilazione",          required: false },
  ];
  return (
    <div className="space-y-1">
      {items.map((it) => (
        <div key={it.key} className="flex items-center gap-2 text-[11px]"
             data-testid={`consenso-${it.key}`}>
          {c[it.key]
            ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
            : <XCircle className={`w-3.5 h-3.5 shrink-0 ${it.required ? "text-red-500" : "text-slate-300"}`} />}
          <span className={c[it.key] ? "text-slate-700" : "text-slate-500"}>
            {it.label}
          </span>
          {it.required && !c[it.key] && (
            <span className="text-[10px] text-red-600 font-semibold">MANCANTE</span>
          )}
        </div>
      ))}
      {c.timestamp && (
        <div className="mt-1.5 pt-1.5 border-t border-slate-100 text-[10px] text-slate-500 space-y-0.5">
          <div>Timestamp: <span className="font-mono">{new Date(c.timestamp).toLocaleString("it-IT")}</span></div>
          {c.privacy_policy_version && (
            <div>Policy: <span className="font-mono">v{c.privacy_policy_version}</span></div>
          )}
          {c.ip_hash && (
            <div title={`Hash: ${c.ip_hash}`}>
              IP hash: <span className="font-mono">{c.ip_hash.slice(0, 8)}…</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Cliente unificato
// ─────────────────────────────────────────────────────────────────
function ClienteUnificatoBlock({ cu }) {
  return (
    <div>
      {cu.ricorrente ? (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-2.5"
             data-testid="cliente-uni-ricorrente">
          <div className="flex items-center gap-1.5 text-amber-900 font-semibold text-[12px]">
            <Flame className="w-4 h-4" />
            Cliente ricorrente · {cu.num_richieste} richieste totali
          </div>
          <div className="text-[10px] text-amber-700 mt-0.5">
            {cu.n_convenzioni} Convenzioni · {cu.n_famiglia} Famiglia/Tempo Libero
          </div>
          {cu.storia_link && (
            <Link to={cu.storia_link}
                  className="inline-flex items-center gap-1 text-[11px] text-amber-900 hover:underline mt-1"
                  data-testid="cliente-uni-storia-link">
              Storia completa <ArrowUpRight className="w-3 h-3" />
            </Link>
          )}
        </div>
      ) : (
        <div className="text-[11px] text-slate-600 py-1">
          Cliente nuovo · {cu.num_richieste} richiesta (questa)
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Documenti
// ─────────────────────────────────────────────────────────────────
function DocumentiBlock({ docs }) {
  if (!docs || docs.length === 0) {
    return <div className="text-[11px] text-slate-400 italic py-1">
      Nessun documento richiesto per questo ramo.
    </div>;
  }
  const caricati = docs.filter((d) => d.caricato);
  const mancanti = docs.filter((d) => !d.caricato);

  const _fmtSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };
  const _apiBase = process.env.REACT_APP_BACKEND_URL || "";

  return (
    <div className="space-y-1">
      <div className="text-[10px] text-slate-500">
        <span className="text-emerald-700 font-semibold">{caricati.length}</span> caricati ·
        <span className="text-slate-400 ml-1">{mancanti.length}</span> non forniti
      </div>
      {/* v1.57: caricati ora hanno bottoni download + preview inline (se PDF/img) */}
      <div className="flex flex-col gap-1.5 mt-1.5">
        {caricati.map((d) => {
          const canPreview = d.mime_type && (d.mime_type.startsWith("application/pdf") || d.mime_type.startsWith("image/"));
          return (
            <div key={d.field || d.id}
                  className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200"
                  data-testid={`doc-caricato-${d.field || d.id}`}>
              <FileText className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[11.5px] font-medium text-emerald-900 truncate">
                  {d.label}
                </div>
                {(d.nome_file || d.size_bytes) && (
                  <div className="text-[10px] text-emerald-700/70 truncate">
                    {d.nome_file || d.id?.slice(0, 8)}
                    {d.size_bytes ? ` · ${_fmtSize(d.size_bytes)}` : ""}
                  </div>
                )}
              </div>
              {d.download_url && (
                <a href={`${_apiBase}${d.download_url}`}
                    target="_blank" rel="noopener noreferrer"
                    className="text-[10.5px] px-2 py-1 rounded bg-emerald-700 text-white hover:bg-emerald-800 font-semibold shrink-0"
                    data-testid={`doc-download-${d.field || d.id}`}
                    title="Scarica documento">
                  Scarica
                </a>
              )}
              {canPreview && d.preview_url && (
                <a href={`${_apiBase}${d.preview_url}`}
                    target="_blank" rel="noopener noreferrer"
                    className="text-[10.5px] px-2 py-1 rounded border border-emerald-700 text-emerald-700 hover:bg-emerald-100 font-semibold shrink-0"
                    data-testid={`doc-preview-${d.field || d.id}`}
                    title="Anteprima inline">
                  Anteprima
                </a>
              )}
            </div>
          );
        })}
        {mancanti.length > 0 && (
          <div className="flex flex-wrap gap-1 pt-1 border-t border-slate-100">
            <span className="text-[10px] text-slate-400 mr-1">Non caricati:</span>
            {mancanti.map((d) => (
              <span key={d.field}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10.5px] bg-slate-50 text-slate-400 border border-slate-200"
                    data-testid={`doc-mancante-${d.field}`}>
                <FileText className="w-3 h-3 opacity-60" />
                {d.label}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Task correlati
// ─────────────────────────────────────────────────────────────────
function TaskBlock({ task, richiestaId }) {
  if (task.total === 0) {
    return <div className="text-[11px] text-slate-400 italic py-1"
                data-testid="task-empty">
      Nessun task aperto.
    </div>;
  }
  return (
    <div>
      <div className="text-[11px] text-amber-800 font-semibold mb-1.5">
        {task.total} {task.total === 1 ? "task aperto" : "task aperti"}
      </div>
      {task.items.map((t) => (
        <div key={t.id}
             className="border-l-2 border-amber-300 pl-2 py-0.5 mb-1"
             data-testid={`task-${t.id}`}>
          <div className="text-[11.5px] text-slate-800 font-medium truncate">{t.titolo}</div>
          <div className="text-[10px] text-slate-500 flex items-center gap-1.5">
            {t.due_date && <span>📅 {new Date(t.due_date).toLocaleDateString("it-IT")}</span>}
            {t.priority && <span className="px-1 rounded bg-slate-100">{t.priority}</span>}
          </div>
        </div>
      ))}
      <Link to={`/admin/richieste?id=${richiestaId}&drawer=tasks`}
             className="text-[11px] text-[#163321] hover:underline inline-flex items-center gap-0.5 mt-1"
             data-testid="task-view-all-link">
        Gestisci task <ArrowUpRight className="w-3 h-3" />
      </Link>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Bottone azione (footer)
// ─────────────────────────────────────────────────────────────────
function AzioneButton({ az, richiestaId }) {
  const [loading, setLoading] = useState(false);
  const [statusModal, setStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState("");

  const isCallable = az.method === "POST" || az.method === "PATCH";

  const handleClick = async () => {
    // Cambio stato → apre inline select
    if (az.testid === "conv-crm-action-change-status") {
      setStatusModal(!statusModal);
      return;
    }
    // External links (open in same tab via <Link>-like nav)
    if (az.external || !isCallable) {
      window.location.href = az.url;
      return;
    }
    // Self-assign POST
    setLoading(true);
    try {
      await axios.request({
        url: `${process.env.REACT_APP_BACKEND_URL}${az.url}`,
        method: az.method,
        data: az.body || {},
        withCredentials: true,
      });
      toast.success(`${az.label} · OK`);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Errore ${az.label}`);
    } finally { setLoading(false); }
  };

  const applyStatusChange = async () => {
    if (!newStatus) return;
    setLoading(true);
    try {
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}${az.url}`,
        { status: newStatus },
        { withCredentials: true },
      );
      toast.success(`Stato aggiornato a "${newStatus}"`);
      setStatusModal(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore cambio stato");
    } finally { setLoading(false); }
  };

  return (
    <div className="relative inline-block">
      <button onClick={handleClick} disabled={loading}
              className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-lg border transition-all disabled:opacity-50 border-[#163321] text-[#163321] hover:bg-[#163321] hover:text-white"
              data-testid={az.testid}>
        {loading && <Loader2 className="w-3 h-3 animate-spin" />}
        {az.label}
        {az.external && <ExternalLink className="w-3 h-3" />}
      </button>
      {statusModal && (
        <div className="absolute top-full mt-1 z-20 bg-white border border-slate-200 rounded-lg shadow-lg p-2 min-w-[200px]"
             data-testid="conv-crm-status-modal">
          <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full text-[12px] border border-slate-200 rounded px-2 py-1 mb-2">
            <option value="">Seleziona nuovo stato…</option>
            <option value="nuovo">Nuovo</option>
            <option value="in_lavorazione">In lavorazione</option>
            <option value="preventivato">Preventivato</option>
            <option value="chiuso_positivo">Chiuso positivo</option>
            <option value="chiuso_negativo">Chiuso negativo</option>
            <option value="archiviato">Archiviato</option>
          </select>
          <button onClick={applyStatusChange} disabled={!newStatus || loading}
                  className="w-full text-[11px] px-2 py-1 rounded bg-[#163321] text-white disabled:opacity-50"
                  data-testid="conv-crm-status-apply">
            Applica
          </button>
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// FASE M — Pannello valutazione automatica Casa (admin re-calc)
// ─────────────────────────────────────────────────────────────────
function CasaValutazioneAdminPanel({ datiWizard }) {
  const [stima, setStima] = useState(datiWizard?.valore_immobile_stima || null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const eurFmt = (n) => n == null ? "—" : new Intl.NumberFormat("it-IT", {
    style: "currency", currency: "EUR", maximumFractionDigits: 0,
  }).format(n);

  const recalc = async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/admin/casa/stima-valore-fabbricato`, {
        mq: Number(datiWizard.mq) || 0,
        tipo_immobile: datiWizard.tipo_immobile || null,
        stato_conservativo: datiWizard.stato_conservativo || null,
        anno_costruzione: datiWizard.anno_costruzione ? Number(datiWizard.anno_costruzione) : null,
        piano: datiWizard.piano != null && datiWizard.piano !== "" ? Number(datiWizard.piano) : null,
        indirizzo_immobile: datiWizard.indirizzo_immobile || null,
        has_box: !!datiWizard.has_box,
        has_giardino: !!datiWizard.has_giardino,
        has_terrazzo: !!datiWizard.has_terrazzo,
      }, { withCredentials: true });
      setStima(data);
      toast.success("Stima ricalcolata");
    } catch (e) {
      const msg = e.response?.data?.detail || "Errore nel ricalcolo";
      setErr(msg); toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const manuale = !!datiWizard?.valore_immobile_manuale;
  const valoreClient = datiWizard?.valore_immobile;

  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-2.5 space-y-1.5 text-[12px]" data-testid="admin-casa-valutazione-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="font-semibold text-emerald-900 flex items-center gap-1.5">
          <TrendingUpIcon /> Valutazione immobile
          {manuale && <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded">MANUALE</span>}
        </div>
        <button onClick={recalc} disabled={loading} className="text-[11px] px-2 py-0.5 rounded bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-50 flex items-center gap-1" data-testid="admin-casa-recalc-btn">
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <>↻</>} Ricalcola
        </button>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        <div><span className="text-slate-600">Valore salvato:</span> <span className="font-semibold text-slate-900">{eurFmt(valoreClient)}</span></div>
        {stima && (
          <>
            <div><span className="text-slate-600">Stima corrente:</span> <span className="font-semibold text-emerald-800" data-testid="admin-casa-stima-value">{eurFmt(stima.valore_stimato)}</span></div>
            <div><span className="text-slate-600">Range:</span> {eurFmt(stima.valore_min)}–{eurFmt(stima.valore_max)}</div>
            <div><span className="text-slate-600">Confidence:</span> <span className={`font-semibold ${stima.confidence === "alta" ? "text-emerald-800" : stima.confidence === "media" ? "text-amber-700" : "text-orange-700"}`}>{stima.confidence?.toUpperCase()}</span></div>
            <div><span className="text-slate-600">Fonte:</span> {stima.fonte_prezzo} ({stima.fonte_dettaglio})</div>
          </>
        )}
      </div>
      {err && <div className="text-red-700 text-[11px]">{err}</div>}
    </div>
  );
}

function TrendingUpIcon() {
  return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>;
}
